// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { useState } from 'react';
import styled from '@emotion/styled';
import Image from 'presentations/views/components/atoms/Image';
import EventHostListDialog from 'presentations/views/components/organisms/EventHostListDialog';
import {
  spacing,
  fontSize,
  mediaMax,
  fontWeight,
  isMediaMax,
  zIndex,
} from 'presentations/views/styles';
import { background, gray01, white } from 'presentations/views/styles/color';
import { EventTypeUserListItem } from 'presentations/api/schema/event_type_user';
import { EventTypeItem } from 'presentations/api/schema/event_type';
import Icon from 'presentations/views/components/atoms/Icon';
import Link from 'presentations/views/components/atoms/Link';
import {
  ProviderType,
  providerTypeIconPath,
  providerTypeName,
} from 'domains/models/account/providerType';
import { HostOperationType } from 'domains/models/event_type/hostOperationType';

export type Props = {
  coverImage: string | null;
  eventType: EventTypeItem;
  teamName: string | null;
  teamImage: string | null;
  host: EventTypeUserListItem;
  hostImage: string | null;
  eventTypeUsers: EventTypeUserListItem[];
};

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const [isOpenEventHostListDialog, setOpenEventHostListDialog] = useState(
    false,
  );

  const isMobile = isMediaMax('tablet');
  const iconSize = isMobile ? '16' : '20';

  const hostImage = props.hostImage;
  const hostName = props.host.name;
  const teamImage = props.teamImage;
  const teamName = props.teamName;

  const otherUsersCount = props.eventTypeUsers.length - 1;

  const link = props.eventType.link;
  const hasLink =
    false ||
    link?.facebookUrl ||
    link?.homepageUrl ||
    link?.instagramUrl ||
    link?.lineUrl ||
    link?.twitterUrl ||
    link?.youtubeUrl;

  const isVisibleOtherHost =
    props.eventType.hostOperationType !== HostOperationType.Team;

  return (
    <>
      <StyledContainer>
        <Content coverImage={props.coverImage} hostImage={props.hostImage}>
          {hostImage && (
            <StyledHostImageContainer>
              <StyledHostImage src={hostImage} alt={hostName || ''} dpr={2} />
              {teamImage && (
                <StyledTeamImage src={teamImage || ''} alt={teamName || ''} />
              )}
            </StyledHostImageContainer>
          )}
          <StyledFlex>
            <StyledHostInfo>
              <StyledHostNameRow>
                {props.eventType.hostOperationType ===
                HostOperationType.Team ? (
                  <HostName>{teamName}</HostName>
                ) : (
                  <HostName>{hostName}</HostName>
                )}
                {otherUsersCount > 0 && isVisibleOtherHost && (
                  <StyledOtherHost
                    onClick={() => {
                      setOpenEventHostListDialog(true);
                    }}
                  >
                    {t('Other contacts', {
                      num: otherUsersCount,
                    })}
                  </StyledOtherHost>
                )}
              </StyledHostNameRow>
              {teamName &&
                props.eventType.hostOperationType ===
                  HostOperationType.None && <TeamName>{teamName}</TeamName>}
            </StyledHostInfo>
            {hasLink && (
              <LinkContainer>
                {props.eventType.link?.twitterUrl && (
                  <SnsLink
                    href={props.eventType.link.twitterUrl}
                    target="_blank"
                  >
                    <Icon name="twitter" size={iconSize} />
                  </SnsLink>
                )}
                {props.eventType.link?.facebookUrl && (
                  <SnsLink
                    href={props.eventType.link.facebookUrl}
                    target="_blank"
                  >
                    <Icon name="facebook" size={iconSize} />
                  </SnsLink>
                )}
                {props.eventType.link?.instagramUrl && (
                  <SnsLink
                    href={props.eventType.link.instagramUrl}
                    target="_blank"
                  >
                    <Icon name="instagram" size={iconSize} />
                  </SnsLink>
                )}
                {props.eventType.link?.youtubeUrl && (
                  <SnsLink
                    href={props.eventType.link.youtubeUrl}
                    target="_blank"
                  >
                    <Icon name="youtube" size={iconSize} />
                  </SnsLink>
                )}
                {props.eventType.link?.homepageUrl && (
                  <SnsLink
                    href={props.eventType.link.homepageUrl}
                    target="_blank"
                  >
                    <Icon name="web" size={iconSize} />
                  </SnsLink>
                )}
                {props.eventType.link?.lineUrl && (
                  <SnsLink href={props.eventType.link.lineUrl} target="_blank">
                    <SnsIcon
                      src={providerTypeIconPath(ProviderType.Line)}
                      alt={providerTypeName(ProviderType.Line)!}
                      width={16}
                      height={16}
                    />
                  </SnsLink>
                )}
              </LinkContainer>
            )}
          </StyledFlex>
        </Content>
      </StyledContainer>
      <EventHostListDialog
        isOpen={isOpenEventHostListDialog}
        eventTypeUsers={props.eventTypeUsers}
        onClickClose={() => setOpenEventHostListDialog(false)}
      />
    </>
  );
}

const StyledContainer = styled.div`
  display: flex;
  flex-grow: 1;
  ${mediaMax.tablet} {
    width: 100%;
  }
`;
const Content = styled.div<{
  coverImage: string | null;
  hostImage: string | null;
}>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  ${mediaMax.desktop} {
    ${(props) =>
      props.coverImage &&
      props.hostImage &&
      `
      margin-top: -${spacing(20)};
    `}
    flex-direction: row;
    align-items: center;
  }
  ${mediaMax.tablet} {
    margin-top: 0;
  }
`;
const StyledFlex = styled.div`
  width: 100%;
  display: block;
  ${mediaMax.desktop} {
    display: flex;
    justify-content: space-between;
  }
  ${mediaMax.tablet} {
    display: block;
  }
`;
const StyledHostInfo = styled.div`
  ${mediaMax.desktop} {
    padding: ${spacing(24)} 0;
  }
  ${mediaMax.tablet} {
    padding: 0;
  }
`;
const StyledHostNameRow = styled.div`
  display: flex;
  align-items: center;
`;
const StyledOtherHost = styled.span`
  display: inline-block;
  margin-left: ${spacing(8)};
  text-decoration: underline;
  cursor: pointer;

  font-size: ${fontSize(16)};
  line-height: 1.2;

  ${mediaMax.tablet} {
    font-size: ${fontSize(14)};
  }
`;
const HostName = styled.div`
  flex: 1;
  font-size: ${fontSize(24)};
  font-weight: ${fontWeight('bold')};
  line-height: 1.2;

  ${mediaMax.tablet} {
    font-size: ${fontSize(18)};
  }
`;
const TeamName = styled.span`
  margin-top: ${spacing(8)};
  font-size: ${fontSize(13)};
  color: ${gray01};

  ${mediaMax.tablet} {
    font-size: ${fontSize(12)};
    margin-top: 0;
    margin-left: 0;
  }
`;

const LinkContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${spacing(12)};
  ${mediaMax.tablet} {
    margin-top: 0;
  }
`;
const SnsLink = styled(Link)`
  color: ${gray01};
  margin-right: ${spacing(16)};
`;

const SnsIcon = styled(Image)`
  vertical-align: top;
`;

const StyledHostImageContainer = styled.div`
  position: relative;
  margin-bottom: ${spacing(12)};
  margin-right: ${spacing(16)};
  background-color: ${background};
  border-radius: 50%;
`;
const StyledHostImage = styled(Image)`
  width: 96px;
  height: 96px;
  border-radius: 50%;
  object-fit: cover;

  ${mediaMax.desktop} {
    width: 140px;
    height: 140px;
    border: 12px solid ${white};
  }
  ${mediaMax.tablet} {
    border: none;
    width: 72px;
    height: 72px;
  }
`;
const StyledTeamImage = styled(Image)`
  position: absolute;
  width: 44px;
  height: 44px;
  right: 0;
  bottom: 0;
  border-radius: 50%;
  object-fit: cover;
  z-index: ${zIndex(100)};

  ${mediaMax.desktop} {
    width: 52px;
    height: 52px;
  }
  ${mediaMax.smartphone} {
    width: 40px;
    height: 40px;
  }
`;
